// MA-288 classroom-roster-identity-write — the registry seam that turns a
// tapped kiosk roster tile into a real, playable local child.
//
// TWO IDENTITY SHAPES, matching migration 0042's two RPCs exactly:
//   CLAIMED  (entry.student_id set): the enrollment already points at a
//   real, PARENT-OWNED students.id. The registry entry gets
//   `supabaseStudentId: null` DELIBERATELY — useCloudSync + the lazy-create
//   path (useActiveStudentId) must stay OFF for this kid on the kiosk
//   device, because that student row belongs to a DIFFERENT auth user (the
//   parent, not the signed-in teacher) and a lazy-create/link write against
//   it would misattribute rows across accounts. Progress on the kiosk
//   device is local-only; attempts reach the cloud exclusively through
//   `insert_kiosk_child_attempt` (see attemptLogger.ts's rpc seam), which
//   is exactly the point of that narrow RPC. `kioskEnrollmentId` +
//   `kioskClaimedStudentId` are the two fields that seam reads.
//
//   UNCLAIMED (entry.student_id null): `create_kiosk_student` mints (or
//   idempotently re-ensures) a teacher-owned shadow `students` row —
//   `parent_id = auth.uid()` is the TEACHER, so the ordinary
//   parent-scoped child_attempts RLS policy already covers writes for it.
//   The registry entry gets a real `supabaseStudentId` (the shadow's id)
//   and NO `kioskClaimedStudentId` — the normal cloud-sync/attempt path
//   already works for it, no RPC seam needed.
//
// Both shapes share a DETERMINISTIC local registry id (`enr_<enrollmentId>`,
// see kioskChildLocalId) so re-tapping the SAME tile — including across a
// page reload — resolves to the SAME local child (childrenStore's
// addChildWithId is the idempotent-upsert primitive this relies on),
// instead of minting a fresh local child (and orphaning the previous one's
// local progress) on every tap.
import { supabase } from "@/supabaseClient.js";
import { isMissingFunctionError } from "./claimApi";
import type { KioskRosterEntry, SupabaseClient } from "./classApi";
// Relative, not "@/childrenStore.js" — kioskIdentity.ts is imported by
// attemptLogger.ts (src/lib/engine), and childrenStore.js does NOT import
// attemptLogger.ts (directly or transitively — verified against
// localAttemptStore.ts/localMilestoneStore.ts, its only two local-engine
// imports), so this direction is cycle-free. childrenStore.js's own
// `listChildren`/JSON-read machinery is reused here as the ONE registry
// reader, rather than re-implementing a second localStorage parser
// alongside it (the brief's "keep ONE reader" instruction).
import { addChildWithId, listChildren, loadProgressForChild, saveProgressForChild } from "../../childrenStore.js";
// Relative and engine-pure (no React, no Supabase): the same queue merge
// App.jsx applies to a family child's cloud row (MA-61).
import { mergeCloudAssignedSkills } from "../engine/assignedSkills";

/** Deterministic local registry id for a kiosk roster tile — same
 *  enrollment id always maps to the same local child, across reloads and
 *  across claimed<->unclaimed transitions (only the CONTENTS of the
 *  registry entry change on a transition, never its id). */
export function kioskChildLocalId(enrollmentId: string): string {
  return `enr_${enrollmentId}`;
}

/**
 * Ensure a local registry child exists for this kiosk roster tile, and
 * return its local id — or null if the door isn't ready for this tile yet
 * (pre-apply: migration 0042's create_kiosk_student RPC doesn't exist).
 *
 * Idempotent: safe to call on every tap, including a re-tap of a tile
 * already ensured this session — addChildWithId updates the existing
 * registry row in place rather than minting a duplicate, and
 * create_kiosk_student itself is idempotent server-side (see its own
 * migration comment).
 *
 * `client` is a test-only DI seam (mirrors classApi.ts's own convention) —
 * production callers never pass it.
 */
export async function ensureKioskChildForEntry(
  entry: KioskRosterEntry,
  ownerUserId: string | null,
  client: SupabaseClient = supabase,
): Promise<string | null> {
  const id = kioskChildLocalId(entry.id);

  if (entry.student_id) {
    // CLAIMED — no RPC call needed; the enrollment row already carries the
    // real students.id. supabaseStudentId is explicitly null (see module
    // header) — this is the field that keeps useCloudSync/lazy-create OFF
    // for this kid on this device.
    addChildWithId({
      id,
      name: entry.display_name,
      ownerUserId,
      supabaseStudentId: null,
      kioskEnrollmentId: entry.id,
      kioskClaimedStudentId: entry.student_id,
    });
    return id;
  }

  // UNCLAIMED — ensure the teacher-owned shadow student server-side first;
  // the registry entry is only written once we have a real shadow id, so a
  // failed/pre-apply call never leaves a half-wired local child behind.
  const { data, error } = await client.rpc("create_kiosk_student", {
    p_enrollment_id: entry.id,
  });
  if (error) {
    // Pre-apply (0042 not yet founder-applied): the door stays
    // display-only for this tile — the existing "not ready yet" copy path
    // in KioskRosterScreen/play.who.tsx, never a thrown error into the UI.
    if (isMissingFunctionError(error)) return null;
    throw error;
  }

  addChildWithId({
    id,
    name: entry.display_name,
    ownerUserId,
    supabaseStudentId: data as string,
    kioskEnrollmentId: entry.id,
    kioskClaimedStudentId: null,
  });
  // MA-938 (D-TUX-25, RED r7 code M1) — the class door carries the teacher's
  // assignment: App.jsx merges a queue only from fetchStudents, which excludes
  // shadows, so the door copies the shadow's queue into this kiosk child's
  // LOCAL assignedSkillIds itself. No cloud write from the door.
  await copyShadowQueueToLocal(id, data as string, client);
  return id;
}

/** MA-938 — reads the shadow's `assigned_skill_ids` (the teacher session owns
 *  the shadow row) and merges it into the kiosk child's local
 *  `progress.assignedSkillIds` (local entries first, de-duplicated, order
 *  kept: mergeCloudAssignedSkills). The selection path
 *  (useTodaysMission -> nextAssignedFocalSkill) then starts the child's next
 *  session on the assigned skill; skills the child has since mastered are
 *  pruned there, so the only-growing shadow queue never re-serves them. A
 *  failed or empty read changes nothing and never blocks the door. Writes
 *  only when the merge adds something. Returns the merged queue (tests). */
/** MA-938 RED r1 S1 — the shadow-queue read never holds a child's tap: after
 *  this long the door opens exactly as on main (nothing copied). supabase-js
 *  sets no fetch timeout of its own. */
export const SHADOW_QUEUE_READ_TIMEOUT_MS = 1500;

export async function copyShadowQueueToLocal(
  localChildId: string,
  shadowId: string,
  client: SupabaseClient = supabase,
  timeoutMs: number = SHADOW_QUEUE_READ_TIMEOUT_MS,
): Promise<string[] | null> {
  let cloudIds: unknown;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const read = (async () => {
      const { data, error } = await client
        .from("students")
        .select("assigned_skill_ids")
        .eq("id", shadowId)
        .maybeSingle();
      if (error || !data) return null;
      return { ids: (data as { assigned_skill_ids?: unknown }).assigned_skill_ids };
    })();
    // A late answer after the timeout is ignored (never written).
    read.catch(() => {});
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), timeoutMs);
    });
    const answer = await Promise.race([read, timeout]);
    if (!answer) return null;
    cloudIds = answer.ids;
  } catch {
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
  const stored = loadProgressForChild(localChildId, () => ({})) as { assignedSkillIds?: unknown };
  const local = Array.isArray(stored.assignedSkillIds) ? (stored.assignedSkillIds as string[]) : [];
  const merged = mergeCloudAssignedSkills(local, cloudIds);
  if (merged.length !== local.length) {
    saveProgressForChild(localChildId, { ...stored, assignedSkillIds: merged });
  }
  return merged;
}

/** The routing decision attemptLogger.ts's logAttempt needs: does THIS
 *  resolved child id belong to a kiosk-CLAIMED registry entry (needs the
 *  narrow insert_kiosk_child_attempt RPC — a plain client upsert would
 *  fail child_attempts_insert's parent_id = auth.uid() check, since the
 *  signed-in caller is the teacher, not the enrollment's owning parent), or
 *  does it not (a family kid, or a kiosk shadow — both already write via
 *  the ordinary upsert, since a shadow's parent_id IS the signed-in
 *  teacher)? */
export interface KioskAttemptRoute {
  kind: "rpc";
  enrollmentId: string;
  studentId: string;
}

/**
 * Reads the ACTIVE registry entry (not just any entry — an attempt is
 * always logged for whichever child is currently active) and checks
 * whether it is a kiosk-CLAIMED entry whose resolved cloud id matches
 * `childId`. `childId` here is whatever id attemptLogger's caller is about
 * to write under — for a kiosk-claimed kid that is, by construction,
 * exactly `activeChild.kioskClaimedStudentId` (persistenceIdentity.js's
 * resolveStudentIdAction resolves to that id verbatim, bypassing
 * lazy-create — see its own comment), so the equality check below is not
 * a loose heuristic, it is the exact identity this seam depends on.
 *
 * Returns null for a family kid (no kiosk fields at all) OR a kiosk
 * shadow (has supabaseStudentId, no kioskClaimedStudentId) — both already
 * write correctly via the plain upsert, so logAttempt's existing branch
 * must stay untouched for them.
 */
interface KioskRegistryChild {
  id: string;
  kioskEnrollmentId?: string | null;
  kioskClaimedStudentId?: string | null;
}

export function resolveKioskAttemptRoute(childId: string | null | undefined): KioskAttemptRoute | null {
  if (!childId) return null;
  const reg = listChildren();
  const children = reg.children as KioskRegistryChild[];
  const active = children.find((c) => c.id === reg.activeChildId);
  if (!active || !active.kioskClaimedStudentId || !active.kioskEnrollmentId) return null;
  if (active.kioskClaimedStudentId !== childId) return null;
  return { kind: "rpc", enrollmentId: active.kioskEnrollmentId, studentId: active.kioskClaimedStudentId };
}
